import React, { useState, useRef, useEffect } from "react";
import { Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNotificationBadge } from "@/hooks/useNotifications";
import NotificationDropdown from "./NotificationDropdown";

interface NotificationBellProps {
    className?: string;
    size?: number;
}

const NotificationBell: React.FC<NotificationBellProps> = ({
    className = "",
    size = 24,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const { badgeText, hasUnread, total } = useNotificationBadge();
    const bellRef = useRef<HTMLDivElement>(null);

    // Close on outside click, and on Escape — the dropdown previously had no
    // keyboard dismissal at all.
    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (bellRef.current && !bellRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setIsOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen]);

    return (
        <div className="relative" ref={bellRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-haspopup="menu"
                /* The count belongs in the name, not in a separate sr-only span:
                   "Notifications" alone told a screen-reader user nothing about
                   whether there was anything to read. */
                aria-label={
                    hasUnread
                        ? `Notifications, ${total} unread`
                        : "Notifications, none unread"
                }
                className={cn(
                    "relative flex items-center justify-center transition-colors duration-150",
                    "text-muted-foreground hover:text-foreground",
                    "outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    isOpen && "text-foreground",
                    // className is applied to the button, not the wrapper — the
                    // header's shared control styling used to land on the
                    // positioning div and never reach anything visible.
                    className
                )}
            >
                <Bell size={size} aria-hidden="true" />

                {hasUnread && (
                    /* Count, not a bare dot: colour alone did not say how many. */
                    <span
                        aria-hidden="true"
                        className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-background bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground tabular-nums"
                    >
                        {badgeText}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 z-50 origin-top-right">
                    <NotificationDropdown onClose={() => setIsOpen(false)} />
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
